<template>
    <div class="w-full flex flex-col items-start relative">
        <div class="w-full mt-1 grid grid-cols-2 gap-2 items-start">
            <div class="relative">
                <Label :for="config.display_name" :class="overrideLabelOpacity ? 'override-opacity' : ''">
                    {{ config.display_name }}
                </Label>
                <div class="mt-2 w-full">
                    <json-forms :data="childData" :schema="childSchema" :uischema="childUiSchema" :renderers="renderers" @change="onJsonChange" />
                </div>
                <div v-if="errors.length" class="text-destructive-foreground mt-1 text-xs">
                    <template v-for="error in errors" :key="error.$uid">
                        <span> * {{ error.$message }} </span>
                    </template>
                </div>
            </div>
            <div class="relative">
                <Tooltip v-if="config.description && hideLateralDescriptionValue">
                    <TooltipTrigger asChild>
                        <span><Info class="h-4 w-4 text-muted-foreground" /></span>
                    </TooltipTrigger>
                    <TooltipContent side="left" class="whitespace-normal max-w-xs">
                        <p class="text-xs" v-html="sanitizeHtml(config.description)" />
                    </TooltipContent>
                </Tooltip>
                <Alert
                    v-if="config.description && !hideLateralDescriptionValue"
                    variant="default"
                    class="min-h-[calc(theme(spacing.10)+theme(spacing.6))] flex items-center p-3 text-xs border bg-muted/10">
                    <AlertDescription class="text-muted-foreground">
                        <span v-html="sanitizeHtml(config.description)" />
                    </AlertDescription>
                </Alert>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUpdated, onUnmounted } from 'vue';
import { JsonForms } from '@jsonforms/vue';
import { vanillaRenderers } from '@jsonforms/vue-vanilla';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Info } from 'lucide-vue-next';
import { sanitizeHtml } from '@/utils/utils';

const props = defineProps({
    config: { type: Object, required: true },
    value: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: true },
    overrideLabelOpacity: { type: Boolean, default: false },
    hideLateralDescription: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] }
});
const emit = defineEmits(['input']);

const childData = ref(props.value || {});
const renderers = Object.freeze([...vanillaRenderers]);

const childSchema = computed(() => {
    const val = props.config.value || {};
    if (val.jsonSchema) return val.jsonSchema;
    if (val.mappings) return val.mappings;
    return { type: 'object', properties: {} };
});

const childUiSchema = computed(() => ({
    type: 'VerticalLayout',
    elements: Object.keys(childSchema.value.properties || {}).map((key) => ({ type: 'Control', scope: `#/properties/${key}` }))
}));

function onJsonChange({ data }) {
    childData.value = data;
}

const hideLateralDescriptionValue = ref(props.hideLateralDescription);
function updateDescriptionVisibility() {
    hideLateralDescriptionValue.value = props.hideLateralDescription || window.innerWidth <= 1024;
}

onMounted(() => {
    updateDescriptionVisibility();
    window.addEventListener('resize', updateDescriptionVisibility);
});
onUpdated(updateDescriptionVisibility);
onUnmounted(() => window.removeEventListener('resize', updateDescriptionVisibility));

watch(
    childData,
    (newVal) => {
        emit('input', newVal);
    },
    { deep: true }
);
</script>
